import React, { useCallback, useEffect, useRef, useState } from 'react';
import { t } from '@/locales/strings';
import { useModalA11y } from '@/hooks/useModalA11y';

/**
 * StreakShareModal — beautiful, viral-friendly share card showing the
 * user's current Be Bold streak. Tap "Share" → opens the native iOS /
 * Android share sheet via Web Share API with the rendered card as an
 * image. Falls back to a PNG download on desktop.
 *
 * The card itself is an inline SVG so it scales crisply at any size
 * and can be exported to PNG without external libraries (serialize →
 * data URI → draw to canvas → blob).
 *
 * Props:
 *   open        — boolean, controls visibility (parent owns state)
 *   onClose     — () => void
 *   streakDays  — number
 *   lang        — 'en' | 'pt'
 *   userName    — optional, shown small under the streak
 */
export default function StreakShareModal({ open, onClose, streakDays = 0, lang = 'en', userName }) {
  const cardRef = useRef(null);
  const dialogRef = useModalA11y({ open, onClose });
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');

  // Guard for the async share flow — if the user closes the modal
  // (or unmounts the component) mid-export, we skip the post-share
  // state updates so the next tap-to-share doesn't find busy=true
  // and do nothing.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  // Reset busy + status whenever the modal closes so a fresh open
  // starts in a clean state even if the previous close happened
  // mid-share.
  useEffect(() => {
    if (!open) {
      setBusy(false);
      setStatus('');
    }
  }, [open]);

  const exportPng = useCallback(async () => {
    const svgEl = cardRef.current?.querySelector('svg');
    if (!svgEl) return null;
    // Serialize the live SVG to a string + data URI. Inline width /
    // height attrs so the rasterizer knows the natural size.
    const w = 1080, h = 1350; // 4:5 portrait — Instagram / TikTok story friendly
    const clone = svgEl.cloneNode(true);
    clone.setAttribute('width', w);
    clone.setAttribute('height', h);
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    const xml = new XMLSerializer().serializeToString(clone);
    const svgBlob = new Blob([xml], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    const loaded = new Promise((res, rej) => {
      img.onload = res;
      img.onerror = rej;
    });
    img.src = url;
    try { await loaded; } catch { URL.revokeObjectURL(url); return null; }
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, w, h);
    URL.revokeObjectURL(url);
    return new Promise((res) => canvas.toBlob(res, 'image/png', 0.95));
  }, []);

  const handleShare = useCallback(async () => {
    setBusy(true);
    setStatus('');
    // Milestone-specific copy trumps the generic "Day {n}" line when
    // the user is sharing exactly at a 7 / 30 / 100 / 365-day mark.
    // Makes the share feel ceremonial instead of stating a fact.
    const milestoneKey = (
      streakDays === 7   ? 'streak_share_text_7'   :
      streakDays === 30  ? 'streak_share_text_30'  :
      streakDays === 100 ? 'streak_share_text_100' :
      streakDays === 365 ? 'streak_share_text_365' :
      'streak_share_text'
    );
    const shareText = t(milestoneKey, lang).replace('{n}', String(streakDays));
    try {
      const blob = await exportPng();
      if (!blob) { setStatus(t('streak_share_error', lang)); return; }
      const file = new File([blob], `bebold-streak-${streakDays}.png`, { type: 'image/png' });
      // Ladder — try each path in order until one works:
      //   1) Web Share API with files (iOS Safari, modern Android)
      //   2) Web Share API text-only (some browsers reject file payloads)
      //   3) Clipboard API write image + text (desktop Chrome / Edge
      //      / Safari with permission)
      //   4) PNG download as last resort (all other browsers)
      const fileShare = { title: t('streak_share_title', lang), text: shareText, files: [file] };
      const textShare = { title: t('streak_share_title', lang), text: shareText };

      if (navigator.canShare && navigator.canShare(fileShare) && navigator.share) {
        await navigator.share(fileShare);
        return;
      }
      if (navigator.share) {
        try {
          await navigator.share(textShare);
          setStatus(t('streak_share_text_only', lang));
          return;
        } catch (e) {
          if (e?.name === 'AbortError') return;
          // fall through to clipboard / download
        }
      }
      // Clipboard — copy text + (when supported) image so the user
      // can paste directly into IG / WhatsApp / a chat window.
      if (navigator.clipboard) {
        try {
          if (window.ClipboardItem && typeof navigator.clipboard.write === 'function') {
            await navigator.clipboard.write([
              new ClipboardItem({ 'image/png': blob, 'text/plain': new Blob([shareText], { type: 'text/plain' }) }),
            ]);
            setStatus(t('streak_share_copied_image', lang));
            return;
          }
          if (typeof navigator.clipboard.writeText === 'function') {
            await navigator.clipboard.writeText(shareText);
            setStatus(t('streak_share_copied_text', lang));
            // Still drop the PNG to downloads so the user has both.
          }
        } catch {
          // Clipboard silently failing → keep going to download.
        }
      }
      // Last resort — PNG download.
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      if (!status) setStatus(t('streak_share_downloaded', lang));
    } catch (e) {
      if (e?.name !== 'AbortError' && mountedRef.current) {
        setStatus(t('streak_share_error', lang));
      }
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  }, [exportPng, lang, streakDays, status]);

  if (!open) return null;

  const dayWord = streakDays === 1
    ? t('streak_share_day_singular', lang)
    : t('streak_share_day_plural', lang);

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="streak-share-title"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 600,
        background: 'rgba(28, 14, 22, 0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
        paddingTop: 'max(20px, env(safe-area-inset-top))',
        paddingBottom: 'max(20px, env(safe-area-inset-bottom))',
        paddingLeft: 'max(20px, env(safe-area-inset-left))',
        paddingRight: 'max(20px, env(safe-area-inset-right))',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
      }}
    >
      <h2 id="streak-share-title" style={{
        position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
        overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', border: 0,
      }}>
        {t('streak_share_title', lang)}
      </h2>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 360,
          background: 'var(--surface, #fff)',
          borderRadius: 24,
          padding: 16,
          boxShadow: '0 24px 60px -20px rgba(45,31,38,0.45)',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
          maxHeight: '92vh',
          overflow: 'hidden',
        }}
      >
        <div ref={cardRef} style={{
          width: '100%',
          aspectRatio: '4 / 5',
          borderRadius: 18,
          overflow: 'hidden',
        }}>
          <ShareCardSvg streakDays={streakDays} dayWord={dayWord} lang={lang} userName={userName} />
        </div>

        {status && (
          <div style={{
            fontSize: 12,
            color: 'var(--ink-dim, #6b5560)',
            textAlign: 'center',
            fontFamily: 'var(--font-ui, Manrope)',
          }}>
            {status}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10 }}>
          <button
            type="button" onClick={onClose}
            style={{
              flex: 1,
              minHeight: 48,
              borderRadius: 999,
              border: '1px solid var(--hair, rgba(45,31,38,0.12))',
              background: 'transparent',
              color: 'var(--ink, #2d1f26)',
              font: '600 14px/1 var(--font-ui, Manrope)',
              cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            {t('streak_share_close', lang)}
          </button>
          <button
            type="button" onClick={handleShare}
            disabled={busy}
            style={{
              flex: 2,
              minHeight: 48,
              borderRadius: 999,
              border: 'none',
              background: busy
                ? 'color-mix(in srgb, var(--primary, #c97a68) 60%, white)'
                : 'linear-gradient(180deg, var(--primary-light, #e3a193) 0%, var(--primary, #c97a68) 100%)',
              color: '#fff',
              font: '600 14px/1 var(--font-ui, Manrope)',
              letterSpacing: '0.02em',
              cursor: busy ? 'wait' : 'pointer',
              WebkitTapHighlightColor: 'transparent',
              boxShadow: '0 10px 24px -10px color-mix(in srgb, var(--primary, #c97a68) 60%, transparent)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M4 12v6a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-6" />
              <path d="M16 6l-4-4-4 4" />
              <path d="M12 2v14" />
            </svg>
            <span>{busy ? t('streak_share_busy', lang) : t('streak_share_btn', lang)}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// ── ShareCardSvg ──────────────────────────────────────────────────
// The image users post to IG / TikTok / WhatsApp. 4:5 portrait, the
// natural aspect for a story / feed share. Pure SVG so it
// rasterises at any size without quality loss.
function ShareCardSvg({ streakDays, dayWord, lang, userName }) {
  return (
    <svg
      viewBox="0 0 1080 1350"
      width="100%"
      height="100%"
      preserveAspectRatio="xMidYMid slice"
      style={{ display: 'block' }}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="bgGrad" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%"  stopColor="#fbeae7" />
          <stop offset="55%" stopColor="#f4d4cf" />
          <stop offset="100%" stopColor="#e3a193" />
        </linearGradient>
        <radialGradient id="auraGrad" cx="50%" cy="50%" r="50%">
          <stop offset="0%"  stopColor="#fff" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#fff" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="flameGrad" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%"  stopColor="#fff5d6" />
          <stop offset="35%" stopColor="#ffb87a" />
          <stop offset="100%" stopColor="#c97a68" />
        </linearGradient>
      </defs>

      <rect width="1080" height="1350" fill="url(#bgGrad)" />
      <ellipse cx="540" cy="640" rx="520" ry="380" fill="url(#auraGrad)" />

      {/* Brand wordmark — top */}
      <text
        x="540" y="160"
        textAnchor="middle"
        fontFamily="Playfair Display, Georgia, serif"
        fontSize="44"
        fontWeight="500"
        fontStyle="italic"
        letterSpacing="14"
        fill="#a85c4b"
      >BE BOLD</text>

      {/* Eyebrow */}
      <text
        x="540" y="430"
        textAnchor="middle"
        fontFamily="Nunito, sans-serif"
        fontSize="34"
        fontWeight="600"
        letterSpacing="14"
        fill="#6b5560"
      >{(lang === 'pt' ? 'SEQUÊNCIA' : 'STREAK')}</text>

      {/* Flame — left of the digit */}
      <g transform="translate(290 760)">
        <path
          d="M 0 0
             C -85 -60, -100 -140, -55 -240
             C -45 -200, -25 -180, -5 -190
             C -10 -240, 35 -300, 80 -340
             C 70 -270, 110 -240, 110 -160
             C 110 -85, 60 0, 0 0 Z"
          fill="url(#flameGrad)"
          opacity="0.96"
        />
        <path
          d="M 0 -10
             C -45 -55, -50 -110, -22 -170
             C -10 -140, 5 -130, 22 -150
             C 18 -180, 50 -210, 78 -245
             C 70 -200, 92 -180, 86 -130
             C 80 -70, 45 -10, 0 -10 Z"
          fill="#fff5d6"
          opacity="0.72"
        />
      </g>

      {/* Streak digit — hero */}
      <text
        x="660" y="800"
        textAnchor="middle"
        fontFamily="Playfair Display, Georgia, serif"
        fontSize="380"
        fontWeight="500"
        letterSpacing="-12"
        fill="#2d1f26"
      >{streakDays}</text>

      {/* Day label */}
      <text
        x="540" y="900"
        textAnchor="middle"
        fontFamily="Playfair Display, Georgia, serif"
        fontSize="58"
        fontStyle="italic"
        fill="#6b5560"
      >{dayWord}</text>

      {/* Optional name */}
      {userName && (
        <text
          x="540" y="1010"
          textAnchor="middle"
          fontFamily="Nunito, sans-serif"
          fontSize="30"
          fontWeight="500"
          letterSpacing="2"
          fill="#a85c4b"
        >{userName}</text>
      )}

      {/* Tagline */}
      <text
        x="540" y={userName ? 1140 : 1100}
        textAnchor="middle"
        fontFamily="Playfair Display, Georgia, serif"
        fontSize="38"
        fontStyle="italic"
        fill="#2d1f26"
      >{lang === 'pt'
        ? 'Treinando meu assoalho pélvico'
        : 'Training my pelvic floor'}
      </text>
      <text
        x="540" y={userName ? 1190 : 1150}
        textAnchor="middle"
        fontFamily="Playfair Display, Georgia, serif"
        fontSize="38"
        fontStyle="italic"
        fill="#2d1f26"
      >{lang === 'pt' ? 'todos os dias.' : 'every day.'}</text>

      {/* Footer URL */}
      <text
        x="540" y="1280"
        textAnchor="middle"
        fontFamily="Nunito, sans-serif"
        fontSize="26"
        fontWeight="600"
        letterSpacing="6"
        fill="#a85c4b"
      >BE-BOLD.APP</text>
    </svg>
  );
}
